// Single frontend source of truth for the concept axis/role set, mirroring the backend
// DACTYL_CONCEPT_TYPES (api/app/Models/DactylConcept.ts) and its derived
// CHARACTERIZATION_AXES (api/app/utils/paperCharacterization/axes.ts). The frontend
// can't import the backend const, so this file mirrors it. Adding an axis here (plus the
// backend DACTYL_CONCEPT_TYPES entry) covers every data-shape/correctness spot on the web
// side. Two presentation-only spots still take a manual per-axis edit: the tab entry in
// MetathesaurusBrowser/index.vue `tabs` and the bespoke button copy in
// DactylConceptList.vue `createLabel` (which otherwise falls back to "New Dactyl concept").
//
// Correctness-critical: the concept editor's tag_scope save/hydrate, the paper
// characterization editor's group shape, and every axisScope picker init derive from
// CHARACTERIZATION_AXES, so a new axis can never be silently dropped on save/load.

// Every concept type (mirror of DACTYL_CONCEPT_TYPES). `normalization` holds scope; the
// rest are characterization / scope-target types.
export const CONCEPT_TYPES = [
	"normalization",
	"disease",
	"anatomy",
	"drug_class",
	"moa",
];

// The characterization axes = every concept type except `normalization` (which holds
// scope rather than being a scope target). Mirror of the backend CHARACTERIZATION_AXES.
export const CHARACTERIZATION_AXES = CONCEPT_TYPES.filter(
	(type) => type !== "normalization",
);

// Human-readable labels, keyed by concept type. Use axisLabel() so an unknown/legacy
// key still renders (falls back to the raw key).
export const CONCEPT_TYPE_LABELS = {
	normalization: "Normalization",
	disease: "Disease",
	anatomy: "Anatomy",
	drug_class: "Drug class",
	moa: "Mechanism of action",
};

export function axisLabel(key) {
	return CONCEPT_TYPE_LABELS[key] || key;
}

// The characterization origins a person or a deterministic rule set - mirror of the
// backend TRUSTED_CHARACTERIZATION_ORIGINS (api/app/utils/paperCharacterization/axes.ts).
// Any other origin (an `ai_suggested` backfill) has not been reviewed by a person.
export const TRUSTED_CHARACTERIZATION_ORIGINS = ["manual", "auto"];

// How a characterization's origin reads beside its term: "manual" was added or
// confirmed by a person, "auto" was set by a rule, anything else is unreviewed.
export function characterizationReviewLabel(origin) {
	if (!TRUSTED_CHARACTERIZATION_ORIGINS.includes(origin)) {
		return "Not yet reviewed";
	}
	return origin === "manual" ? "Reviewed" : "Set automatically";
}

// Fresh { axis: array } map over the characterization axes, hydrated from `source` (each
// axis defaults to [] when absent or not an array). Used to normalize a server
// characterization response so a new axis is never silently dropped. New arrays each call.
export function axisGroups(source = {}) {
	return Object.fromEntries(
		CHARACTERIZATION_AXES.map((axis) => [
			axis,
			Array.isArray(source?.[axis]) ? source[axis] : [],
		]),
	);
}

// Fresh { axis: [] } map over the characterization axes (new arrays each call, so it is
// safe as a component data() initializer).
export function emptyAxisArrays() {
	return axisGroups();
}

// Fresh { axis: null } map over the characterization axes (for one-shot picker state).
export function emptyAxisNulls() {
	return Object.fromEntries(CHARACTERIZATION_AXES.map((axis) => [axis, null]));
}
